import React, { useEffect, useState } from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import PropTypes from "prop-types";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import GavelIcon from "@material-ui/icons/Gavel";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import { APP_STRINGS } from "constants/appStrings";

const SWIPE_THRESHOLD = 48;

const PORTRAIT_SLIDES = [
  {
    src: require("assets/img/ana-lucena-1.jpeg"),
    alt: "Ana Lucena em retrato editorial 1",
    objectPosition: "center 16%",
  },
  {
    src: require("assets/img/ana-lucena-2.jpeg"),
    alt: "Ana Lucena em retrato editorial 2",
    objectPosition: "center 18%",
  },
  {
    src: require("assets/img/ana-lucena-3.jpeg"),
    alt: "Ana Lucena em retrato editorial 3",
    objectPosition: "center 18%",
  },
  {
    src: require("assets/img/ana-lucena-4.jpeg"),
    alt: "Ana Lucena em retrato editorial 4",
    objectPosition: "center 20%",
  },
];

const useStyles = makeStyles((theme) => ({
  section: {
    width: "min(1240px, calc(100% - 48px))",
    margin: "0 auto",
    padding: theme.spacing(8, 0, 2),
    [theme.breakpoints.down("sm")]: {
      width: "min(100% - 32px, 1240px)",
      paddingTop: theme.spacing(6),
    },
  },
  label: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderRadius: 999,
    background: "rgba(212, 179, 127, 0.14)",
    color: "#8b6a3d",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  title: {
    margin: theme.spacing(2.5, 0, 2),
    fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    fontSize: "clamp(2.8rem, 4.6vw, 4.6rem)",
    lineHeight: 0.92,
    letterSpacing: "-0.03em",
    color: "#5d3342",
  },
  lead: {
    maxWidth: 620,
    fontSize: 18,
    lineHeight: 1.9,
    color: "#5f6775",
  },
  ctas: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    flexWrap: "wrap",
  },
  primaryButton: {
    minHeight: 54,
    padding: theme.spacing(0, 3),
    borderRadius: 999,
    background: "linear-gradient(135deg, #8d4d61 0%, #a75e74 100%)",
    color: "#fff8ef",
    boxShadow: "0 20px 40px rgba(98, 54, 69, 0.18)",
    "&:hover": {
      background: "linear-gradient(135deg, #7e4557 0%, #99566c 100%)",
    },
  },
  secondaryButton: {
    minHeight: 54,
    padding: theme.spacing(0, 3),
    borderRadius: 999,
    border: "1px solid rgba(31, 38, 50, 0.14)",
    color: "#6a3d4c",
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(2),
    marginTop: theme.spacing(5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  statCard: {
    padding: theme.spacing(2.5),
    borderRadius: 26,
    background: "rgba(255,255,255,0.78)",
    border: "1px solid rgba(31, 38, 50, 0.08)",
    boxShadow: "0 26px 50px rgba(22, 29, 40, 0.08)",
  },
  statValue: {
    fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    fontSize: 42,
    lineHeight: 1,
    color: "#181c22",
  },
  statLabel: {
    marginTop: theme.spacing(1),
    color: "#646c7a",
    lineHeight: 1.7,
  },
  portraitWrap: {
    position: "relative",
    height: "100%",
    minHeight: 600,
    [theme.breakpoints.down("sm")]: {
      minHeight: 460,
      marginTop: theme.spacing(2),
    },
  },
  portraitCard: {
    position: "absolute",
    inset: 0,
    borderRadius: 34,
    overflow: "hidden",
    background:
      "radial-gradient(circle at top, rgba(255,255,255,0.96) 0%, rgba(238,229,222,0.94) 100%)",
    boxShadow: "0 34px 80px rgba(98, 54, 69, 0.14)",
  },
  portraitTrack: {
    position: "relative",
    width: "100%",
    height: "100%",
  },
  portraitSlide: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transform: "scale(1.02)",
    transition: "opacity 700ms ease, transform 1200ms ease",
  },
  portraitSlideActive: {
    opacity: 1,
    transform: "scale(1)",
  },
  portraitImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center center",
    display: "block",
  },
  portraitChrome: {
    position: "absolute",
    inset: 0,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    padding: theme.spacing(2.5),
    pointerEvents: "none",
    zIndex: 2,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  portraitCounter: {
    display: "inline-flex",
    alignItems: "center",
    padding: theme.spacing(0.9, 1.4),
    borderRadius: 999,
    background: "rgba(33, 22, 28, 0.42)",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    color: "#fff7ed",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.12em",
    backdropFilter: "blur(10px)",
  },
  portraitControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    pointerEvents: "auto",
    [theme.breakpoints.down("xs")]: {
      justifyContent: "space-between",
    },
  },
  portraitNavButton: {
    minWidth: 0,
    width: 52,
    height: 52,
    borderRadius: "50%",
    padding: 0,
    color: "#fffaf3",
    background: "rgba(33, 22, 28, 0.44)",
    border: "1px solid rgba(255, 255, 255, 0.14)",
    backdropFilter: "blur(12px)",
    boxShadow: "0 14px 34px rgba(18, 10, 14, 0.22)",
    "&:hover": {
      background: "rgba(33, 22, 28, 0.62)",
    },
  },
  portraitDots: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.25),
    borderRadius: 999,
    background: "rgba(255, 248, 239, 0.72)",
    boxShadow: "0 12px 24px rgba(30, 18, 24, 0.12)",
  },
  portraitDot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    border: "none",
    padding: 0,
    background: "rgba(110, 78, 89, 0.32)",
    cursor: "pointer",
    transition: "transform 180ms ease, background 180ms ease",
  },
  portraitDotActive: {
    background: "#8d4d61",
    transform: "scale(1.35)",
  },
  floatingCard: {
    position: "absolute",
    right: 20,
    bottom: 20,
    width: "min(320px, calc(100% - 40px))",
    padding: theme.spacing(2.5),
    borderRadius: 24,
    background: "rgba(108, 59, 75, 0.82)",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    color: "#f7f2ea",
    boxShadow: "0 24px 50px rgba(7, 10, 16, 0.28)",
    backdropFilter: "blur(10px)",
  },
  floatingTitle: {
    fontSize: 13,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#d8bb8f",
    fontWeight: 700,
  },
  floatingText: {
    marginTop: theme.spacing(1.5),
    fontSize: 15,
    lineHeight: 1.8,
    color: "rgba(247, 242, 234, 0.78)",
  },
  blocks: {
    marginTop: theme.spacing(6),
  },
  videoShowcase: {
    marginTop: theme.spacing(5),
    padding: theme.spacing(4),
    borderRadius: 34,
    background: "linear-gradient(135deg, #6d3d4d 0%, #8b5163 100%)",
    color: "#f7f2ea",
    boxShadow: "0 34px 80px rgba(98, 54, 69, 0.16)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
    },
  },
  videoLabel: {
    display: "inline-flex",
    padding: theme.spacing(1, 2),
    borderRadius: 999,
    background: "rgba(255,255,255,0.08)",
    color: "#ead7b3",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  videoTitle: {
    margin: theme.spacing(2, 0, 1.5),
    fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    fontSize: "clamp(2.2rem, 3.8vw, 3.4rem)",
    lineHeight: 0.98,
    color: "#fff7ed",
  },
  videoText: {
    maxWidth: 780,
    color: "rgba(247, 242, 234, 0.8)",
    fontSize: 17,
    lineHeight: 1.9,
    marginBottom: theme.spacing(3),
  },
  videoCard: {
    borderRadius: 28,
    overflow: "hidden",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    boxShadow: "0 22px 50px rgba(53, 26, 35, 0.2)",
  },
  video: {
    width: "100%",
    display: "block",
    aspectRatio: "16 / 9",
    objectFit: "cover",
    background: "#2f1620",
  },
  videoCaption: {
    padding: theme.spacing(1.5, 2),
    color: "rgba(247, 242, 234, 0.72)",
    fontSize: 14,
    lineHeight: 1.7,
  },
  infoCard: {
    height: "100%",
    padding: theme.spacing(3.5),
    borderRadius: 30,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(250,246,240,0.92) 100%)",
    border: "1px solid rgba(31, 38, 50, 0.08)",
    boxShadow: "0 26px 60px rgba(22, 29, 40, 0.08)",
  },
  infoIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 56,
    height: 56,
    borderRadius: 18,
    background: "rgba(212, 179, 127, 0.16)",
    color: "#8c6a3e",
    marginBottom: theme.spacing(2),
  },
  infoTitle: {
    fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    fontSize: 34,
    color: "#5d3342",
    marginBottom: theme.spacing(1.5),
  },
  infoText: {
    color: "#5f6775",
    fontSize: 16,
    lineHeight: 1.85,
    marginBottom: theme.spacing(2),
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  chip: {
    background: "#fff",
    border: "1px solid rgba(31, 38, 50, 0.08)",
    color: "#343b47",
    fontWeight: 600,
  },
}));

export default function SectionApresentation({ rollPageOnClickButton }) {
  const classes = useStyles();
  const [activeSlide, setActiveSlide] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchCurrentX, setTouchCurrentX] = useState(null);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveSlide((currentSlide) =>
        currentSlide === PORTRAIT_SLIDES.length - 1 ? 0 : currentSlide + 1
      );
    }, 4800);

    return () => window.clearInterval(intervalId);
  }, []);

  const handlePreviousSlide = () => {
    setActiveSlide((currentSlide) =>
      currentSlide === 0 ? PORTRAIT_SLIDES.length - 1 : currentSlide - 1
    );
  };

  const handleNextSlide = () => {
    setActiveSlide((currentSlide) =>
      currentSlide === PORTRAIT_SLIDES.length - 1 ? 0 : currentSlide + 1
    );
  };

  const handleTouchStart = (event) => {
    setTouchStartX(event.touches[0].clientX);
    setTouchCurrentX(event.touches[0].clientX);
  };

  const handleTouchMove = (event) => {
    if (touchStartX === null) {
      return;
    }

    setTouchCurrentX(event.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartX === null || touchCurrentX === null) {
      setTouchStartX(null);
      setTouchCurrentX(null);
      return;
    }

    const swipeDistance = touchStartX - touchCurrentX;

    if (Math.abs(swipeDistance) >= SWIPE_THRESHOLD) {
      if (swipeDistance > 0) {
        handleNextSlide();
      } else {
        handlePreviousSlide();
      }
    }

    setTouchStartX(null);
    setTouchCurrentX(null);
  };

  return (
    <div className={classes.section}>
      <GridContainer alignItems="center" spacing={4}>
        <GridItem xs={12} md={6}>
          <span className={classes.label}>
            <VerifiedUserIcon fontSize="small" />
            {APP_STRINGS.presentation.label}
          </span>

          <Typography component="h2" className={classes.title}>
            {APP_STRINGS.presentation.title}
          </Typography>

          <Typography component="p" className={classes.lead}>
            {APP_STRINGS.presentation.lead}
          </Typography>

          <Box className={classes.ctas}>
            <Button
              className={classes.primaryButton}
              onClick={() => rollPageOnClickButton("contact")}
            >
              {APP_STRINGS.presentation.primaryCta}
            </Button>
            <Button
              className={classes.secondaryButton}
              onClick={() => rollPageOnClickButton("skills")}
            >
              {APP_STRINGS.presentation.secondaryCta}
            </Button>
          </Box>

          <div className={classes.statGrid}>
            {APP_STRINGS.presentation.stats.map((stat) => (
              <div key={stat.value} className={classes.statCard}>
                <div className={classes.statValue}>{stat.value}</div>
                <div className={classes.statLabel}>{stat.label}</div>
              </div>
            ))}
          </div>
        </GridItem>

        <GridItem xs={12} md={6}>
          <div className={classes.portraitWrap}>
            <div
              className={classes.portraitCard}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className={classes.portraitTrack}>
                {PORTRAIT_SLIDES.map((slide, index) => {
                  const isActive = activeSlide === index;

                  return (
                    <div
                      key={slide.src}
                      className={`${classes.portraitSlide} ${
                        isActive ? classes.portraitSlideActive : ""
                      }`}
                      aria-hidden={!isActive}
                    >
                      <img
                        className={classes.portraitImage}
                        src={slide.src}
                        alt={slide.alt || APP_STRINGS.presentation.portraitAlt}
                        style={{ objectPosition: slide.objectPosition }}
                      />
                    </div>
                  );
                })}
              </div>

              <div className={classes.portraitChrome}>
                <div className={classes.portraitControls}>
                  <div className={classes.portraitCounter}>
                    {String(activeSlide + 1).padStart(2, "0")} /{" "}
                    {String(PORTRAIT_SLIDES.length).padStart(2, "0")}
                  </div>

                  <Button
                    className={classes.portraitNavButton}
                    onClick={handlePreviousSlide}
                    aria-label="Imagem anterior"
                  >
                    <ChevronLeftIcon />
                  </Button>

                  <div className={classes.portraitDots}>
                    {PORTRAIT_SLIDES.map((slide, index) => (
                      <button
                        key={slide.alt}
                        type="button"
                        className={`${classes.portraitDot} ${
                          activeSlide === index ? classes.portraitDotActive : ""
                        }`}
                        onClick={() => setActiveSlide(index)}
                        aria-label={`Ir para imagem ${index + 1}`}
                      />
                    ))}
                  </div>

                  <Button
                    className={classes.portraitNavButton}
                    onClick={handleNextSlide}
                    aria-label="Proxima imagem"
                  >
                    <ChevronRightIcon />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </GridItem>
      </GridContainer>

      <GridContainer spacing={4} className={classes.blocks}>
        <GridItem xs={12} md={6}>
          <div className={classes.infoCard}>
            <div className={classes.infoIcon}>
              <VerifiedUserIcon />
            </div>
            <Typography component="h3" className={classes.infoTitle}>
              {APP_STRINGS.presentation.credentialsTitle}
            </Typography>
            <Typography component="p" className={classes.infoText}>
              {APP_STRINGS.presentation.credentialsText}
            </Typography>
            <div className={classes.chipRow}>
              {APP_STRINGS.presentation.credentialsItems.map((item) => (
                <Chip key={item} label={item} className={classes.chip} />
              ))}
            </div>
          </div>
        </GridItem>

        <GridItem xs={12} md={6}>
          <div className={classes.infoCard}>
            <div className={classes.infoIcon}>
              <GavelIcon />
            </div>
            <Typography component="h3" className={classes.infoTitle}>
              {APP_STRINGS.presentation.specialtiesTitle}
            </Typography>
            <Typography component="p" className={classes.infoText}>
              {APP_STRINGS.presentation.specialtiesText}
            </Typography>
            <div className={classes.chipRow}>
              {APP_STRINGS.presentation.specialtiesItems.map((item) => (
                <Chip key={item} label={item} className={classes.chip} />
              ))}
            </div>
          </div>
        </GridItem>
      </GridContainer>

      <div className={classes.videoShowcase}>
        <span className={classes.videoLabel}>
          {APP_STRINGS.presentation.videoBadge}
        </span>
        <Typography component="h3" className={classes.videoTitle}>
          {APP_STRINGS.presentation.videoTitle}
        </Typography>

        <div className={classes.videoCard}>
          <video
            className={classes.video}
            src={require("assets/img/new-layout/video-02.mp4")}
            poster={require("assets/img/new-layout/AL.png")}
            controls
            playsInline
            muted
            loop
            preload="metadata"
          />
          <div className={classes.videoCaption}>
            {APP_STRINGS.presentation.videoCaption}
          </div>
        </div>
      </div>
    </div>
  );
}

SectionApresentation.propTypes = {
  rollPageOnClickButton: PropTypes.func.isRequired,
};
